// dSpr Self-Review - question schema v1
//
// Frontend-owned schema (per Q1 lean - hardcoded for v1). Backend doesn't
// need to know about this; only the form page imports it. If HR ever wants
// to manage questions through a UI, this gets promoted to a DB table with
// the same shape.
//
// Question types - must match the renderer dispatch in dSpr_ReviewForm.js:
//   text                                - single textarea
//   single_choice                       - radio buttons
//   single_choice_with_text             - radio + textarea
//   single_choice_with_text_and_upload  - radio + textarea + file upload
//                                         (upload widget hidden in Phase A
//                                          per Q7 lean - text portion only)
//   text_with_upload                    - textarea + file upload
//                                         (upload widget hidden in Phase A)
//   rating_1_to_5_with_text             - 1-5 buttons + textarea
//   number_pair_with_text               - two number inputs + textarea
//                                         (used for the delivery-rate question)
//
// Answer shapes stored in dspr_review_responses.answer_json:
//   text:               { value: "…" }
//   single_choice:      { choice: <index 0..n-1> }
//   single_choice_with_text*:  { choice: <index>, text: "…" }
//   rating_1_to_5_with_text:   { rating: <1..5>, text: "…" }
//   number_pair_with_text:     { num1: <int>, num2: <int>, text: "…" }
//   *_with_upload:      same as above; attachments tracked separately in
//                       dspr_review_attachments (Phase B).

const DSPR_QUESTION_SCHEMA = {
    version: "v1",
    sections: [
        // ── Section 1 - Team Excellence ──────────────────────────────────────
        {
            id: "team_excellence",
            title: "Team Excellence",
            subtitle: "Collaboration, leave conduct, proactiveness · 4 items",
            color: "team",
            questions: [
                {
                    id: "q1", number: 1, type: "text", required: true,
                    title: "How would you describe your contribution as a team player during this period?",
                    description: "Think about: helping teammates, sharing knowledge, covering during absences, or stepping in on project tasks.",
                    placeholder: "Describe specific instances where you collaborated, assisted, or went beyond your own tasks to support the team…",
                },
                {
                    id: "q2", number: 2, type: "single_choice", required: true,
                    title: "Were there any escalations or behavioural concerns raised against you by team members this half-year?",
                    options: [
                        "No escalations - I maintained positive working relationships throughout",
                        "One instance - it was resolved and documented",
                        "There were concerns - I'm addressing them proactively",
                    ],
                },
                {
                    id: "q3", number: 3, type: "single_choice_with_text", required: true,
                    title: "How well did you follow the Leave Policy and Backup Identification Protocol?",
                    description: "Did you inform your home manager in advance, identify a backup, and send a proper handover before leaves?",
                    options: [
                        "Fully compliant - backup identified and handover done for all leaves",
                        "Mostly compliant - minor gaps, no business impact",
                        "Had unscheduled leaves - explained below",
                    ],
                    textPlaceholder: "If any unscheduled leaves occurred, briefly describe the context and how you managed coverage…",
                },
                {
                    id: "q4", number: 4, type: "text", required: true,
                    title: "Describe a situation where you proactively flagged a risk or potential escalation before it became a problem.",
                    description: "If no such instance occurred, reflect on why and what you would do differently.",
                    placeholder: "Describe the situation, what you flagged, to whom, and what outcome it helped avoid…",
                },
            ],
        },

        // ── Section 2 - Operational Excellence ───────────────────────────────
        {
            id: "operational_excellence",
            title: "Operational Excellence",
            subtitle: "Compliance, timesheets, documentation · 3 items",
            color: "ops",
            questions: [
                {
                    id: "q5", number: 5, type: "single_choice", required: true,
                    title: "Did you submit all Dolluz Corp timesheets on time during this half-year?",
                    options: [
                        "Yes - 100% on-time submission throughout",
                        "Mostly - one or two late submissions with valid reason",
                        "No - I had consistent delays (I will explain below)",
                    ],
                },
                {
                    id: "q6", number: 6, type: "single_choice_with_text_and_upload", required: true,
                    title: "Did you complete all mandatory Dolluz Corp compliance trainings and org-level requirements?",
                    options: [
                        "Yes - completed 100% of assigned trainings",
                        "Partially - some are in progress",
                        "No - I had blockers (I will explain below)",
                    ],
                    textPlaceholder: "If incomplete, briefly state reason and timeline for completion…",
                    uploadLabel: "Upload completion certificates or screenshots (optional)",
                },
                {
                    id: "q7", number: 7, type: "rating_1_to_5_with_text", required: true,
                    title: "How would you rate your adherence to Dolluz Corp's process documentation requirements this half-year?",
                    description: "1 = Did not meet expectations · 5 = Fully met all requirements.",
                    scaleLowLabel: "Did not meet",
                    scaleHighLabel: "Fully met",
                    textPlaceholder: "Briefly justify your rating with a specific example…",
                },
            ],
        },

        // ── Section 3 - Customer Excellence ──────────────────────────────────
        {
            id: "customer_excellence",
            title: "Customer Excellence",
            subtitle: "Delivery, client conduct, upskilling · 5 items",
            color: "cust",
            questions: [
                {
                    id: "q8", number: 8, type: "number_pair_with_text", required: true,
                    title: "What is your task delivery rate for this half-year? (Scheduled tasks vs delivered)",
                    description: "Target is ≥ 95%. Enter numbers below - if you don't have the exact figure, provide your best estimate with context.",
                    input1Placeholder: "Total tasks scheduled",
                    input2Placeholder: "Tasks delivered",
                    textPlaceholder: "If below 95%, explain the reasons and what you did to mitigate impact…",
                },
                {
                    id: "q9", number: 9, type: "single_choice_with_text", required: true,
                    title: "How consistent was your participation in daily touchpoints and ad-hoc / war room calls?",
                    options: [
                        "Zero misses - attended all daily touchpoints and ad-hoc calls",
                        "Occasional misses - with prior intimation",
                        "Frequent misses - I will explain below",
                    ],
                    textPlaceholder: "If any misses occurred, note the reason and whether it was communicated in advance…",
                },
                {
                    id: "q10", number: 10, type: "text_with_upload", required: true,
                    title: "Did you receive any client appreciation during this half-year? If yes, describe the context.",
                    description: "Include appreciations from your onshore/offshore client team, business manager, or Dolluz Corp supervisor.",
                    placeholder: "Describe the appreciation - what you did, who recognised it, and the outcome it contributed to…",
                    uploadLabel: "Upload appreciation emails or screenshots (recommended)",
                },
                {
                    id: "q11", number: 11, type: "single_choice_with_text", required: true,
                    title: "Were there any client escalations (onshore or offshore) against you during this half-year?",
                    options: [
                        "No escalations - maintained zero-tolerance standards throughout",
                        "One incident - it was addressed and closed",
                        "Multiple incidents - explained below",
                    ],
                    textPlaceholder: "If any escalation occurred, describe what happened, how it was resolved, and what you learned…",
                },
                {
                    id: "q12", number: 12, type: "text_with_upload", required: true,
                    title: "What client-specific trainings, courses, or upskilling did you complete this half-year?",
                    description: "List mandatory trainings completed, optional courses pursued, and any certifications earned.",
                    placeholder: "List each training / course with the completion date and whether it was mandatory or self-initiated…",
                    uploadLabel: "Upload completion certificates (optional)",
                },
            ],
        },

        // ── Section 4 - Growth & Development ─────────────────────────────────
        {
            id: "growth_development",
            title: "Growth & Development",
            subtitle: "Self-reflection and goals for next half · 4 items",
            color: "grow",
            questions: [
                {
                    id: "q13", number: 13, type: "text", required: true,
                    title: "What are your top 3 achievements from this half-year that you are most proud of?",
                    description: "Be specific - describe the outcome, the impact, and your direct contribution to it.",
                    placeholder: "1. Achievement one - describe outcome and impact\n2. Achievement two - describe outcome and impact\n3. Achievement three - describe outcome and impact",
                },
                {
                    id: "q14", number: 14, type: "text", required: true,
                    title: "What is one area where you feel you could have performed better? What held you back?",
                    placeholder: "Be honest - reflect on a genuine gap, the reasons behind it, and what you plan to do differently…",
                },
                {
                    id: "q15", number: 15, type: "text", required: true,
                    title: "What are your 2-3 focus goals for the next half-year?",
                    description: "These could be skill goals, delivery targets, or behavioural improvements.",
                    placeholder: "1. Goal one\n2. Goal two\n3. Goal three (optional)",
                },
                {
                    id: "q16", number: 16, type: "text", required: false,
                    title: "Is there anything you want your home manager to know that is not covered above?",
                    description: "Additional context, support needed, or anything you'd like discussed in your 1:1.",
                    placeholder: "Any additional comments, context, or requests for your home manager…",
                },
            ],
        },
    ],
};

// Client / Project FALLBACK list - used only if the live API fetch from
// dspr_client_project_options fails (network hiccup at form load). The
// real list is managed via the Cycle Scheduler → Manage Options modal.
// "Other" is always at the end and is reserved server-side too.
const CLIENT_PROJECT_FALLBACK = [
    "WellSky",
    "Internal / Bench",
    "dEPR",
    "dSlip",
    "Other",
];

// Flat list of all question ids for required-check loops on submit.
const ALL_QUESTION_IDS = DSPR_QUESTION_SCHEMA.sections.flatMap(s => s.questions.map(q => q.id));
const REQUIRED_QUESTION_IDS = DSPR_QUESTION_SCHEMA.sections.flatMap(
    s => s.questions.filter(q => q.required).map(q => q.id)
);

// CJS exports - backend require()s this file directly (PDF generator + server
// routes). Frontend imports (`import { DSPR_QUESTION_SCHEMA } from "..."`)
// still work via webpack's CJS↔ESM interop in CRA.
module.exports = {
    DSPR_QUESTION_SCHEMA,
    CLIENT_PROJECT_FALLBACK,
    ALL_QUESTION_IDS,
    REQUIRED_QUESTION_IDS,
};
